// src/components/ProdMgrs/components/ProdMgrsMonthlyNetSalesRow.jsx
// =============================================================================
// BLOCK PMGR.MNS.ROW.0 — ProdMgrs Monthly Net Sales row (self-contained)
// Purpose:
//  - Do NOT depend on ../../shared/months.js or ../../common/FormatDollarAmount.jsx
//  - Render monthly net sales row from API shape
// Notes:
//  - Backend query returns: { category, Jan..Dec, Total } (often numeric strings)
//  - We normalize values safely and format as USD.
// =============================================================================

import React from 'react';
import PropTypes from 'prop-types';

import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

// =============================================================================
// BLOCK PMGR.MNS.ROW.1 — Constants
// =============================================================================
const MONTH_KEYS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// =============================================================================
// BLOCK PMGR.MNS.ROW.2 — Formatting helpers
// =============================================================================
function toNum(v) {
  if (typeof v === 'number') {
    return Number.isFinite(v) ? v : 0;
  }
  if (typeof v === 'string') {
    const n = Number(v.trim());
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function fmtUSD(v) {
  const n = toNum(v);
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function getField(row, key) {
  // supports either "Jan" or "jan" just in case something got normalized differently
  if (!row || typeof row !== 'object') {
    return 0;
  }
  if (row[key] !== undefined) {
    return row[key];
  }
  const lower = key.toLowerCase();
  if (row[lower] !== undefined) {
    return row[lower];
  }
  return 0;
}

// =============================================================================
// BLOCK PMGR.MNS.ROW.3 — Component
// =============================================================================
function ProdMgrsMonthlyNetSalesRow({ netSale }) {
  const category = netSale?.category ?? netSale?.Category ?? '';

  return (
    <TableRow hover>
      <TableCell data-cy={`pmgr-monthly-net-sales-category-${category}`}>{category}</TableCell>

      {MONTH_KEYS.map((k) => (
        <TableCell key={k} align="right" data-cy={`pmgr-monthly-net-sales-${k}-${category}`}>
          {fmtUSD(getField(netSale, k))}
        </TableCell>
      ))}

      <TableCell align="right" data-cy={`pmgr-monthly-net-sales-total-${category}`}>
        {fmtUSD(getField(netSale, 'Total'))}
      </TableCell>
    </TableRow>
  );
}

ProdMgrsMonthlyNetSalesRow.propTypes = {
  netSale: PropTypes.shape({
    category: PropTypes.string,
    Category: PropTypes.string,

    // months + total can be numbers or numeric strings
    Jan: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Feb: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Mar: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Apr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    May: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Jun: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Jul: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Aug: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Sep: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Oct: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Nov: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Dec: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};

export default React.memo(ProdMgrsMonthlyNetSalesRow);
